import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Flame, Coins, Trophy, Star, Swords } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import RoasterAvatar from "../components/RoasterAvatar";

const login = () => {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function RoasterProfilePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [tipping, setTipping] = useState(false);
  const [custom, setCustom] = useState("");

  const load = async () => { try { setP((await api.get(`/roasters/${id}/profile`)).data); } catch (e) { setErr(true); } };
  useEffect(() => { load(); }, [id]);

  const tip = async (amount) => {
    if (!user) return login();
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    setTipping(true);
    try {
      const r = (await api.post(`/roasters/${id}/tip`, { amount: amt })).data;
      toast.success(`Tipped ₹${amt} — ${p.handle} gets ₹${r.net} 💸`);
      setCustom(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Tip failed"); }
    finally { setTipping(false); }
  };

  return (
    <div className="min-h-screen grain relative overflow-hidden">
      <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] rounded-full bg-brand/20 blur-[120px] pointer-events-none" />
      <header className="relative z-10 max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2"><Flame className="text-brand" size={24} /><span className="font-heading font-bold text-xl tracking-tighter">RoastLit</span></a>
      </header>
      <main className="relative z-10 max-w-2xl mx-auto px-6 py-10" data-testid="roaster-profile-page">
        {err ? <div className="text-center text-zinc-400 py-20">Roaster not found.</div> : !p ? <div className="text-zinc-400">Loading…</div> : (
          <>
            <div className="flex items-center gap-4">
              <RoasterAvatar avatar={p.avatar} initials={(p.handle || "R").replace("@", "").slice(0, 2).toUpperCase()} size={64} ring="#ff4500" />
              <div>
                <h1 className="font-heading font-bold text-3xl">{p.handle}</h1>
                <div className="text-sm font-bold tracking-widest uppercase text-brand">{p.rank} roaster</div>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
              {[
                { icon: Trophy, label: "Wins", value: p.wins, color: "#ff4500" },
                { icon: Coins, label: "Tips", value: `₹${(p.total_tips || 0).toFixed(0)}`, color: "#d4af37" },
                { icon: Swords, label: "Battle wins", value: p.battle_wins || 0, color: "#22d3ee" },
                { icon: Star, label: "Rating", value: p.rating ? p.rating.toFixed(1) : "—", color: "#34d399" },
              ].map((s) => (
                <div key={s.label} className="rounded-sm border border-white/10 bg-zinc-900/60 p-4">
                  <s.icon size={16} style={{ color: s.color }} />
                  <div className="font-heading font-bold text-2xl mt-1">{s.value}</div>
                  <div className="text-xs text-zinc-500 uppercase tracking-widest">{s.label}</div>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-xl border border-[#d4af37]/30 bg-[#d4af37]/5 p-5" data-testid="profile-tip">
              <div className="flex items-center gap-2 text-[#d4af37] font-bold mb-3"><Coins size={18} /> Tip {p.handle}</div>
              <div className="grid grid-cols-4 gap-2">
                {(p.tip_presets || [20, 50, 100, 200]).map((a) => (
                  <button key={a} data-testid={`profile-tip-${a}`} onClick={() => tip(a)} disabled={tipping}
                    className="py-2.5 rounded-sm border border-[#d4af37]/40 bg-[#d4af37]/10 text-[#d4af37] font-bold hover:bg-[#d4af37]/20 disabled:opacity-50">₹{a}</button>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <input data-testid="profile-tip-custom" type="number" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Custom ₹" className="flex-1 bg-zinc-900 border border-zinc-800 rounded-sm p-2.5 text-sm focus:border-[#d4af37] focus:outline-none" />
                <button data-testid="profile-tip-send" onClick={() => tip(custom)} disabled={tipping} className="px-5 py-2.5 rounded-full bg-[#d4af37] text-black font-bold disabled:opacity-50">Tip</button>
              </div>
            </div>

            {p.recent_roasts?.length > 0 && (
              <div className="mt-8">
                <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-3">Recent published roasts</div>
                <div className="space-y-3">
                  {p.recent_roasts.map((r, i) => (
                    <div key={i} className="rounded-sm border border-white/10 bg-zinc-900/60 p-4">
                      <p className="text-sm text-zinc-100">"{r.roast_text}"</p>
                      <div className="text-xs text-zinc-500 mt-1">▲ {r.upvotes} upvotes</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
